<template>
  <section class="admin-content">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div class="container">
        <div class="d-flex align-items-center text-white" style="padding: 32px 0; gap: 16px;">
          <div class="header-icon-wrap">
            <i class="fe fe-check-circle" style="font-size: 22px;"></i>
          </div>
          <div>
            <h4 class="mb-0 font-weight-bold">Complete Registration</h4>
            <small style="opacity: .8;">Upload photo and complete payment to finalise enrolment</small>
          </div>
        </div>
      </div>
    </div>

    <div class="container mt-n3 pb-5">

      <!-- ── Enrollee Photo Card ── -->
      <div class="card shadow table-card mb-4">
        <div class="form-card-header">
          <div class="section-number"><i class="fe fe-camera" style="font-size: 12px;"></i></div>
          <h6 class="mb-0 font-weight-bold text-white">Enrollee Photo</h6>
        </div>
        <div class="card-body py-4">
          <div class="d-flex flex-column align-items-center" style="gap: 16px;">
            <img :src="`https://claimify-api.hayokmedicare.ng/image/${auth_user.user_image}`"
              onerror="this.onerror=null; this.src='/assets/img/zamfara.jpg'" class="rounded-circle"
              style="width: 100px; height: 100px; object-fit: cover; border: 3px solid #2563eb; box-shadow: 0 4px 12px rgba(37,99,235,0.15);" />
            <p class="mb-0 font-weight-bold" style="font-size: 1rem;">{{ auth_user.full_name }}</p>
            <p class="text-muted mb-0" style="font-size: .85rem;">{{ auth_user.id_card_number }}</p>

            <div class="d-flex flex-wrap justify-content-center" style="gap: 8px;">
              <button class="btn btn-sm btn-outline-primary" @click="takePicAndroid">
                <i class="fe fe-camera mr-1"></i> Take Photo
              </button>
              <label class="btn btn-sm btn-outline-secondary mb-0" style="cursor: pointer;">
                <i class="fe fe-upload mr-1"></i> Upload Picture
                <input type="file" class="d-none" v-on:change="attachPic" />
              </label>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Payment Summary Card ── -->
      <div class="card shadow table-card mb-4">
        <div class="form-card-header">
          <div class="section-number"><i class="fe fe-credit-card" style="font-size: 12px;"></i></div>
          <h6 class="mb-0 font-weight-bold text-white">Payment Summary</h6>
        </div>
        <div class="card-body p-0">
          <ul class="list-unstyled mb-0">
            <li class="info-row">
              <i class="fe fe-user info-icon"></i>
              <div class="flex-grow-1">
                <p class="info-label">Enrollee</p>
                <p class="info-value mb-0">{{ auth_user.full_name }} <span class="text-muted">({{
                    auth_user.id_card_number }})</span></p>
                <p class="mb-0 text-muted" style="font-size: .8rem;">{{ auth_user.sector }}</p>
              </div>
              <div class="text-right">
                <p class="info-label">Premium</p>
                <p class="info-value mb-0 font-weight-bold">&#8358;{{ getPlan.amount | numeral(0, 0) }}</p>
              </div>
            </li>
            <li class="info-row">
              <i class="fe fe-mail info-icon"></i>
              <div class="flex-grow-1">
                <p class="info-label">Confirmation Email</p>
                <input type="text" class="form-control form-control-sm mt-1" v-model="auth_user.email"
                  placeholder="Email address for receipt" style="max-width: 280px;" />
              </div>
            </li>
            <li class="info-row">
              <i class="fe fe-percent info-icon"></i>
              <div class="flex-grow-1">
                <p class="info-label">Transaction Fee</p>
              </div>
              <p class="info-value mb-0">&#8358;{{ (totalCost - getPlan.amount) | numeral(0, 0) }}</p>
            </li>
            <li class="info-row" style="background: #f0f4ff; border-radius: 0 0 12px 12px;">
              <i class="fe fe-dollar-sign info-icon text-primary"></i>
              <div class="flex-grow-1">
                <p class="info-label">Total Payable</p>
              </div>
              <p class="mb-0 font-weight-bold text-primary" style="font-size: 1.15rem;">&#8358;{{ totalCost | numeral(0,
                0) }}</p>
            </li>
          </ul>
        </div>
      </div>

      <!-- ── Share Link Card ── -->
      <div class="card shadow table-card mb-4">
        <div class="card-body py-3 px-4">
          <label class="small font-weight-bold text-secondary mb-2">
            <i class="fe fe-link mr-1"></i> Share Payment Link
          </label>
          <div class="input-group">
            <input disabled type="text"
              :value="`https://app.claimify.ng/subscribe/${$route.params.agency_id}/${auth_user.id}`"
              class="form-control form-control-sm" />
            <div class="input-group-append">
              <button class="btn btn-outline-secondary btn-sm" @click="writeToClipboard()">
                <i class="fe fe-copy mr-1"></i> Copy
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- ── Payment Actions ── -->
      <div class="card shadow table-card mb-4">
        <div class="card-body py-4 px-4">
          <button class="btn btn-submit btn-block mb-3" style="font-size: 1rem; padding: 14px;">
            <paystack :amount="totalCost * 100" :email="auth_user.email" :subaccount="agency.virtual_account"
              :paystackkey="paystackkey" :reference="reference" :callback="callback" :first_name="auth_user.firstname"
              :last_name="auth_user.lastname" :phone="auth_user.phone_number" :close="close" :embed="false"
              class="col-md-12">
              <i class="fe fe-credit-card mr-2"></i> Pay with Paystack
            </paystack>
          </button>
          <button v-if="user != null" class="btn btn-outline-secondary btn-block" @click="$router.go(-1)">
            <i class="fe fe-skip-forward mr-1"></i> Continue without Payment
          </button>
        </div>
      </div>

    </div>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </section>
</template>

<script>
  import { randomAlphaNumeric } from "@/utils/secureRandom";
  // Import component
  import Loading from "vue-loading-overlay";
  // Import stylesheet
  import "vue-loading-overlay/dist/vue-loading.css";
  // Init plugin
  import paystack from "vue-paystack";
  // import AddDependentVoluntary from "@/views/clients/AddDependentVoluntary.vue";
  import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
  import { Clipboard } from "@capacitor/clipboard";
  import agencySubscriptionAmounts from "@/json/agency_subscription_amounts.json";
  // let agency = JSON.parse(localStorage.getItem("agency"));

  export default {
    components: {
      Loading,
      paystack,
      // AddDependentVoluntary,
    },
    data() {
      return {
        hmos: "",
        agency_id: "",
        agency: "",
        provider_id: "",
        auth_user: {},
        getPlan: {
          name: "Nil",
          amount: 0,
        },
        totalCost: 0,
        payment_type: "online",
        showpay: false,
        showpic: true,
        showdependent: false,
        subscription_amounts: agencySubscriptionAmounts,
        plan_id: null,
        // paystackkey: "pk_test_6555651ecaf3d32f6c6a49dd81f875f06be5f332", //paystack public key
        paystackkey: "pk_live_873084ab876aad40fe062000ee5a706de1b16f30", //paystack public key
        providers: "",
        singleplan: "",
        plans: "",
        image: "",
        imagefile: "",
        myplan: "",
        sms_message:
          "Thank you for choosing Us. You can come to our office to collect your ID Card in 3 weeks.",
        user: null,
        isLoading: false,
        fullPage: true,
        windowwith: "",
      };
    },
    beforeMount() {
      this.windowwith = window.innerWidth * 0.75;
      this.user = JSON.parse(localStorage.getItem("user"));
    },
    computed: {
      // getPlan() {
      //   let formatter = this.selected_plan.filter(
      //     (x) => x.plan == this.auth_user.sector
      //   );
      //   console.log(formatter);
      //   return formatter[0];
      // },

      // totalCost() {
      //   let charges = 0.015 * parseInt(this.getPlan.amount);

      //   let cappedCharges = charges > 2000 ? 2000 : charges;

      //   return this.getPlan.amount + cappedCharges;
      // },

      reference() {
        return randomAlphaNumeric(100);
      },
    },
    methods: {
      async calcPlan() {
        let match = this.subscription_amounts.find(
          (x) => String(x.agency_id) === String(this.$route.params.agency_id)
        );

        this.getPlan = {
          name: "Individual Plan",
          amount: match ? match.amount : 0,
        };
      },
      async calcCost() {
        let charges = 0.015 * parseInt(this.getPlan.amount);

        let insucare_charge = 0.01 * parseInt(this.getPlan.amount);

        // let cappedCharges = charges > 2000 ? 2000 : charges;

        this.totalCost = this.getPlan.amount + charges + 100 + insucare_charge;
      },

      callback: function (response) {
        // this.sendSMS();
        this.makeSubscribe();
        console.log(response);
      },
      close: function () {
        console.log("Payment closed");
      },
      attachPic(event) {
        this.user = JSON.parse(localStorage.getItem("user"));
        console.log(event);
        this.image = event.target.files[0];
        this.uploadPicture();
      },
      uploadPicture() {
        this.isLoading = true;
        this.user = JSON.parse(localStorage.getItem("user"));
        let formData = new FormData();
        formData.append("user_image", this.image);
        formData.append("user_id", this.$route.params.id);
        this.axios
          .post("/api/v1/uploadUserImage", formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then((response) => {
            console.log(response);

            this.isLoading = false;

            this.getUser();
            this.$toasted.info("Image added Successfully!", {
              position: "top-center",
              duration: 3000,
            });
          });
      },

      makeSubscribe() {
        this.user = JSON.parse(localStorage.getItem("user"));
        this.isLoading = true;
        this.axios
          .post("/api/v1/make/transaction", {
            agency_id: this.$route.params.agency_id,
            amount: this.getPlan.amount,
            description: "Online Plan Payment",
            type: "online",
            status: "created",
            transaction_ref: this.reference,
            user_id: this.$route.params.id,
          })
          .then((response) => {
            console.log(response);
            this.$toasted.info("Congratulations you have successfully enrolled", {
              position: "top-center",
              duration: 8000,
            });
            this.isLoading = false;
            this.$router.push(`/subscribe-success/${this.$route.params.id}`);
          })
          .catch((error) => {
            console.log(error.response);
          });
      },

      async takePicAndroid() {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Camera,
        });

        let imageUrl = image.base64String;
        this.imagefile = "data:image/png;base64," + imageUrl;
        this.savePic();
      },

      savePic() {
        this.isLoading = true;
        this.axios
          .post(`/api/v1/uploadcustomerpicImage`, {
            user_image: this.imagefile,
            user_id: this.$route.params.id,
          })
          .then((response) => {
            console.log(response);
            this.getUser();
            this.$toasted.success("Picture uploaded Successfully", {
              position: "top-center",
              duration: 3000,
            });

            this.showpay = true;
            this.showpic = false;

            this.isLoading = false;
          })
          .catch((error) => {
            console.error(error);
            this.isLoading = false;
          });
      },

      getHMO() {
        this.axios
          .get(`/api/v1/auth/hmos/${this.$route.params.agency_id}`)
          .then((response) => {
            this.agency = response.data.data;
            console.log(response);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      sendSMS() {
        // Disabled: this called multitexter.com's SMS API with a hardcoded
        // credential embedded in frontend source (a leaked secret visible to
        // every browser). Re-enable only via a backend endpoint that holds
        // the credential server-side.
        console.warn("sendSMS() is disabled pending a backend SMS endpoint.");
      },
      getUser() {
        this.axios
          .get(`/api/v1/user-no-auth/${this.$route.params.id}`)
          .then((response) => {
            this.auth_user = response.data.user;
            console.log(this.auth_user);
          })
          .catch((error) => {
            console.error(error);
          });
      },
      async writeToClipboard() {
        await Clipboard.write({
          string: `https://app.claimify.ng/subscribe/${this.$route.params.agency_id}/${this.auth_user.id}`,
        });

        this.$toasted.info("Copied to Clipboard!", {
          position: "top-center",
          duration: 3000,
        });
      },
    },

    created() {
      this.getHMO();
      this.getUser();
      this.calcPlan();
      this.calcCost();
    },
  };
</script>
<style scoped>
  .page-header {
    background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  }
</style>